import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NgClickOutsideDirective } from 'ng-click-outside2';

let nextPanelId = 0;

export type StatusTone =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export interface StatusFilterOption {
  /** Value emitted via selectionChange. Use `null` for an "All" option. */
  value: string | null;
  /** Visible label. */
  title: string;
  /** Material Symbols icon name. */
  icon: string;
  /** Colour tone for the indicator dot. Defaults to 'neutral'. */
  tone?: StatusTone;
}

@Component({
  selector: 'app-status-filter',
  standalone: true,
  imports: [NgClickOutsideDirective],
  templateUrl: './status-filter.component.html',
  styleUrl: './status-filter.component.css',
})
export class StatusFilterComponent {
  @Input({ required: true }) options: StatusFilterOption[] = [];
  @Input() selectedValue: string | null = null;
  @Input() label: string = 'Status';
  @Input() triggerIcon: string = 'filter_alt';
  @Input() panelAlign: 'left' | 'right' = 'left';
  @Input() panelWidth: string = '240px';
  /** Optional fallback label when nothing is selected (e.g. "All"). */
  @Input() allLabel: string = 'All';

  @Output() selectionChange = new EventEmitter<string | null>();

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  isOpen = false;
  readonly panelId = `status-filter-menu-${++nextPanelId}`;

  get selectedOption(): StatusFilterOption | undefined {
    return this.options.find((o) => o.value === this.selectedValue);
  }

  get selectedTitle(): string {
    return this.selectedOption?.title ?? this.allLabel;
  }

  get selectedTone(): StatusTone {
    return this.selectedOption?.tone ?? 'neutral';
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** Opens and moves focus to the chosen option (else the first), as a menu button does. */
  open() {
    this.isOpen = true;
    setTimeout(() => {
      const items = this.items();
      (items.find((item) => item.getAttribute('aria-checked') === 'true') ?? items[0])?.focus();
    });
  }

  /** `returnFocus` hands focus back to the trigger (keyboard close, selection). */
  close(returnFocus = false) {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (returnFocus) this.trigger?.nativeElement.focus();
  }

  select(value: string | null) {
    this.selectionChange.emit(value);
    this.close(true);
  }

  isActive(option: StatusFilterOption): boolean {
    return option.value === this.selectedValue;
  }

  openFromKeyboard(event: Event) {
    event.preventDefault();
    if (!this.isOpen) this.open();
  }

  /** Arrows, Home and End move between options; Tab closes back to the trigger. */
  onPanelKeydown(event: KeyboardEvent) {
    const items = this.items();
    if (!items.length) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(index < 0 ? items.length - 1 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusAt(items.length - 1);
        break;
      case 'Tab':
        event.preventDefault();
        this.close(true);
        break;
    }
  }

  private items(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('.statusPanel [role="menuitemradio"]')
    );
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (!this.isOpen) return;
    this.close(this.host.nativeElement.contains(document.activeElement));
  }
}
